import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/apiService';
import {
    STATUS_LABEL, STATUS_CLASS, ORIGEM_LABEL, ACAO_LABEL, brDateTime, tempoRestante, nomeFase, diffConfigs, marcarConferida,
} from './promocoesWhitelabelUtil';
import { valeNaHora, entraGuardado, nomeItem, DiffTable } from './PromocaoDiffPanel';
import './Reclamacoes.css';
import './PromocoesWhitelabel.css';

/**
 * Revisão de uma promoção de whitelabel, em etapas: Itens → Diferenças → Resumo.
 *
 * Só o "Aprovar e aplicar" do Resumo muda o ambiente, e vai com o login de quem decide (a API
 * recusa o token do sistema e confere o tema do MCP). Regras que a tela precisa deixar claras:
 * - editar um config ATIVO vale na hora para os vendedores que ele alcança;
 * - config novo, ou editado que está inativo, entra guardado: só vale depois de ativado;
 * - tudo ou nada: se alguém editou o alvo depois da proposta, a aprovação é recusada inteira.
 */

const STEPS = [
    { key: 'itens', label: '1. Itens' },
    { key: 'diferencas', label: '2. Diferenças' },
    { key: 'resumo', label: '3. Resumo' },
];

function Modal({ title, children, footer, wide, onClose }) {
    return (
        <div className="vr-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>
            <div className={`vr-modal ${wide ? 'md' : ''}`}>
                <div className="vr-modal-head">{title}</div>
                <div className="vr-modal-body">{children}</div>
                <div className="vr-modal-foot">{footer}</div>
            </div>
        </div>
    );
}


function ItemCard({ it, onDiff }) {
    const estado = it.acao === 'recusado' ? 'st-down' : valeNaHora(it) ? 'st-changed' : it.acao === 'sem_mudanca' ? 'st-denied' : 'st-justified';
    return (
        <div className={`vr-q ${estado}`}>
            <div className="vr-q-row" style={{ cursor: 'default' }}>
                <div className="vr-q-text">
                    <div className="wl-item-title">
                        <span className="vr-chip">{it.nivel === 'fase' ? 'Fase' : 'Empresa'}</span>
                        {nomeItem(it)}
                    </div>
                    <div className="vr-muted vr-small wl-item-facts">
                        <span>Na origem: config #{it.origem_config_id ?? '—'}{it.origem_active ? ' (ativo lá)' : ''}</span>
                        <span>Aqui: {it.target_config_id ? `config #${it.target_config_id}` : 'não existe — será criado'}</span>
                        {it.alcance && <span title={it.alcance.base}>Alcança: {it.alcance.vendedores} vendedor{it.alcance.vendedores === 1 ? '' : 'es'} ({it.alcance.base})</span>}
                    </div>
                    {it.motivo && <div className="wl-motivo">{it.motivo}</div>}
                    {it.mudou_desde_a_proposta && (
                        <div className="wl-motivo">Este config foi alterado aqui depois da proposta: a aprovação será recusada. Peça uma proposta nova.</div>
                    )}
                </div>
                <div className="vr-q-side">
                    <span className={`vr-chip ${it.acao === 'recusado' ? 'vr-chip-denied' : it.acao === 'sem_mudanca' ? '' : 'vr-chip-partial'}`}>{ACAO_LABEL[it.acao] || it.acao}</span>
                    {valeNaHora(it) && <span className="vr-chip wl-chip-live">ATIVO · vale na hora</span>}
                    {entraGuardado(it) && <span className="vr-chip">entra guardado</span>}
                    {(it.acao === 'criar' || it.acao === 'editar') && (
                        <button className="vr-btn vr-btn-sm" onClick={onDiff}>Ver diferenças ›</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function PromocaoRevisao() {
    const { id, step = 'itens' } = useParams();
    const navigate = useNavigate();
    const [p, setP] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [decisao, setDecisao] = useState(null);       // {pode, motivo, email}
    const [sel, setSel] = useState(null);               // id do item na etapa Diferenças
    const [side, setSide] = useState('proposto');
    const [nota, setNota] = useState('');
    const [conferi, setConferi] = useState(false);
    const [modal, setModal] = useState(null);
    const [sending, setSending] = useState(false);
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    const load = useCallback(async () => {
        setLoadError(null);
        try {
            const r = await apiService.whitelabelPromotionGet(id);
            if (!r || !r.cabecalho || !Array.isArray(r.itens)) throw new Error('resposta inesperada da proposta');
            setP(r);
        } catch (e) {
            setLoadError(e.response?.data?.detail || e.message || 'Erro ao carregar a proposta');
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        (async () => {
            try { setDecisao(await apiService.whitelabelPromotionCanDecide()); } catch (e) {
                setDecisao({ pode: false, motivo: e.response?.data?.detail || 'Não foi possível conferir sua permissão.', relogin: e.response?.data?.relogin });
            }
        })();
    }, []);

    useEffect(() => { if (p && step === 'resumo') marcarConferida(id); }, [p, step, id]);

    const itens = useMemo(() => (p ? p.itens : []), [p]);
    const aplicaveis = itens.filter((it) => it.acao === 'criar' || it.acao === 'editar');
    const itemSel = aplicaveis.find((it) => it.id === sel) || aplicaveis[0];
    const diff = useMemo(() => {
        if (!itemSel) return { itens: [], truncado: false };
        const base = itemSel.acao === 'criar' ? {} : (itemSel.atual && itemSel.atual.config) || {};
        return diffConfigs(base, itemSel.config);
    }, [itemSel]);

    const goStep = (key) => { navigate(`/promocoes-whitelabel/${id}/${key}`); window.scrollTo(0, 0); };
    const batchId = p && p.cabecalho ? p.cabecalho.batch_id : null;
    const backToList = () => navigate(batchId ? `/promocoes-whitelabel-lote/${batchId}` : '/promocoes-whitelabel');

    const decidir = async (acao) => {
        setSending(true);
        try {
            const r = await apiService.whitelabelPromotionDecide(acao, id, nota);
            if (!r || !r.status) throw new Error('resposta inesperada');
            notify(acao === 'approve' ? 'Proposta aplicada ✓' : acao === 'undo' ? 'Proposta desfeita ✓' : 'Proposta recusada', 'success');
            setP(null);
            await load();
            goStep('resumo');
        } catch (e) {
            const data = e.response?.data || {};
            const det = data.detail;
            setModal({
                kind: 'erro', relogin: !!data.relogin, conflito: e.response?.status === 409,
                text: typeof det === 'object' && det ? det.mensagem : det || e.message || 'Erro ao decidir',
                conflitos: (det && det.conflitos) || [],
            });
        } finally {
            setSending(false);
        }
    };

    const reentrar = async () => {
        try { await apiService.logout(); } catch (e) { /* segue para o login de todo jeito */ }
        window.location.href = `/login?redirect=${encodeURIComponent(`/promocoes-whitelabel/${id}/resumo`)}`;
    };

    if (loadError) {
        return (
            <div className="vr-page"><div className="vr-list">
                <button className="vr-btn" onClick={backToList}>‹ Voltar às propostas</button>
                <div className="vr-card" style={{ marginTop: 14 }}>{loadError}</div>
            </div></div>
        );
    }
    if (!p) return <div className="vr-page"><div className="vr-list">Carregando proposta…</div></div>;

    const c = p.cabecalho;
    const pendente = c.status === 'pendente';
    const podeDecidir = decisao && decisao.pode;
    const temAoVivo = aplicaveis.some(valeNaHora);
    const conflitoPrevisto = itens.some((it) => it.mudou_desde_a_proposta);

    let content = null;
    if (step === 'itens') {
        content = (
            <>
                {itens.map((it) => <ItemCard key={it.id} it={it} onDiff={() => { setSel(it.id); goStep('diferencas'); }} />)}
            </>
        );
    } else if (step === 'diferencas') {
        content = aplicaveis.length === 0 ? <div className="vr-card">Nenhum item desta proposta muda o conteúdo.</div> : (
            <>
                <div className="vr-qfilters">
                    {aplicaveis.map((it) => (
                        <button key={it.id} className={`vr-pill-filter ${itemSel && itemSel.id === it.id ? 'active' : ''}`} onClick={() => setSel(it.id)}>
                            {nomeItem(it)}
                        </button>
                    ))}
                </div>
                <div className="vr-card">
                    <h3>{nomeItem(itemSel)} · {itemSel.acao === 'criar' ? 'config novo (tudo é adicionado)' : `contra o config #${itemSel.target_config_id} daqui`}</h3>
                    {valeNaHora(itemSel) && <div className="vr-mbox warn">Este config está <strong>ATIVO</strong> aqui: as diferenças abaixo passam a valer para os vendedores assim que a proposta for aprovada.</div>}
                    <DiffTable diff={diff} />
                </div>
            </>
        );
    } else {
        const grupo = (titulo, lista, vazio, cls) => (
            <div className="vr-card">
                <h3>{titulo} ({lista.length})</h3>
                {lista.length === 0 ? <div className="vr-muted">{vazio}</div> : lista.map((it) => (
                    <div key={it.id} className={`vr-beh-row ${cls}`}>
                        <span className="vr-beh-name">{nomeItem(it)}</span>
                        <span className="vr-muted vr-small">{it.motivo || (it.target_config_id ? `config #${it.target_config_id}` : 'novo')}{it.alcance ? ` · ${it.alcance.vendedores} vendedores` : ''}</span>
                    </div>
                ))}
            </div>
        );
        content = (
            <>
                {grupo('Vale na hora ao aprovar', aplicaveis.filter(valeNaHora), 'Nenhum item edita um config ativo.', 'd-down')}
                {grupo('Entra guardado (precisa ativar depois)', aplicaveis.filter(entraGuardado), 'Nenhum.', 'd-eq')}
                {grupo('Não muda nada', itens.filter((it) => it.acao === 'sem_mudanca'), 'Nenhum.', 'd-eq')}
                {grupo('Recusados na proposta', itens.filter((it) => it.acao === 'recusado'), 'Nenhum.', 'd-eq')}
                {pendente && (
                    <div className="vr-card">
                        <h3>Observação da decisão (opcional)</h3>
                        <textarea id="wl-nota" className="vr-textarea" value={nota} onChange={(e) => setNota(e.target.value)}
                            placeholder="Fica registrada junto com quem decidiu." />
                    </div>
                )}
            </>
        );
    }

    let footer;
    if (step === 'itens') {
        footer = (<>
            <button className="vr-btn" onClick={backToList}>‹ Propostas</button>
            <span className="vr-footer-info">Nada é aplicado até a aprovação no Resumo.</span>
            <button className="vr-btn vr-btn-primary" onClick={() => goStep('diferencas')}>Ver diferenças ›</button>
        </>);
    } else if (step === 'diferencas') {
        footer = (<>
            <button className="vr-btn" onClick={() => goStep('itens')}>‹ Voltar</button>
            <span className="vr-footer-info">Diferença calculada contra o que está valendo aqui agora.</span>
            <button className="vr-btn vr-btn-primary" onClick={() => goStep('resumo')}>Continuar para o resumo ›</button>
        </>);
    } else {
        footer = (<>
            <button className="vr-btn" onClick={() => goStep('diferencas')}>‹ Voltar</button>
            <span className="vr-footer-info">
                {pendente ? (podeDecidir ? 'Só ao aprovar o ambiente muda.' : (decisao ? decisao.motivo : 'Conferindo sua permissão…'))
                    : `${STATUS_LABEL[c.status]}${c.decided_by_email ? ` por ${c.decided_by_email} em ${brDateTime(c.decided_at)}` : ''}.`}
            </span>
            {pendente && <button className="vr-btn vr-btn-danger-outline" disabled={!podeDecidir || sending} onClick={() => setModal({ kind: 'recusar' })}>Recusar</button>}
            {pendente && <button className="vr-btn vr-btn-success" disabled={!podeDecidir || sending || aplicaveis.length === 0} onClick={() => { setConferi(false); setModal({ kind: 'aprovar' }); }}>{sending ? 'Aplicando…' : 'Aprovar e aplicar'}</button>}
            {c.status === 'aplicada' && <button className="vr-btn vr-btn-danger-outline" disabled={!podeDecidir || sending} onClick={() => setModal({ kind: 'desfazer' })}>Desfazer</button>}
        </>);
    }

    const sidePanel = step === 'diferencas' && itemSel ? (
        <aside className="vr-side wl-side">
            <div className="vr-side-head">
                {[['proposto', 'Proposto'], ['atual', 'Aqui hoje'], ['dados', 'Dados']].map(([k, l]) => (
                    <button key={k} className={`vr-side-tab ${side === k ? 'active' : ''}`} onClick={() => setSide(k)}>{l}</button>
                ))}
            </div>
            <div className="vr-side-body">
                {side === 'proposto' && <pre className="vr-pre">{JSON.stringify(itemSel.config, null, 2)}</pre>}
                {side === 'atual' && (itemSel.atual && itemSel.atual.config
                    ? <pre className="vr-pre">{JSON.stringify(itemSel.atual.config, null, 2)}</pre>
                    : <div className="vr-muted">Não existe aqui: será criado, guardado.</div>)}
                {side === 'dados' && (
                    <dl className="wl-dados">
                        <dt>md5 proposto</dt><dd>{itemSel.config_md5}</dd>
                        <dt>md5 aqui na proposta</dt><dd>{itemSel.target_md5 || '—'}</dd>
                        <dt>md5 aqui agora</dt><dd>{(itemSel.atual && itemSel.atual.md5) || '—'}</dd>
                        <dt>ativo aqui</dt><dd>{itemSel.atual ? (itemSel.atual.active ? 'sim' : 'não') : '—'}</dd>
                        <dt>origem</dt><dd>{ORIGEM_LABEL[c.origem]} · config #{itemSel.origem_config_id ?? '—'}</dd>
                        {itemSel.nivel === 'fase' && <><dt>fase aqui</dt><dd>{nomeFase(itemSel)} (#{itemSel.phase_id})</dd></>}
                    </dl>
                )}
            </div>
        </aside>
    ) : null;

    return (
        <div className="vr-page vr-review">
            {toast && <div className={`vr-toast ${toast.type}`}>{toast.msg}</div>}
            <div className="vr-sticky">
                <div className="vr-head">
                    <button className="vr-back" onClick={backToList} title="Voltar às propostas">‹</button>
                    <div className="vr-head-main">
                        <div className="vr-head-title">
                            <h1>Proposta #{c.id} · {c.subdomain}</h1>
                            <span className="vr-chip vr-chip-var">{ORIGEM_LABEL[c.origem]} → {ORIGEM_LABEL[c.destino]}</span>
                            <span className={`vr-chip ${STATUS_CLASS[c.status] || ''}`}>{STATUS_LABEL[c.status] || c.status}</span>
                            {temAoVivo && pendente && <span className="vr-chip wl-chip-live">edita config ativo</span>}
                        </div>
                        <div className="vr-head-facts">
                            <span>Proposta por <strong>{c.proposed_by_email}</strong></span>
                            <span>em <strong>{brDateTime(c.created)}</strong></span>
                            {pendente && <span><strong>{tempoRestante(c.expires_at)}</strong></span>}
                            <span>{aplicaveis.length} de {itens.length} configs mudam algo</span>
                        </div>
                        {c.observacao && <div className="vr-head-ids">“{c.observacao}”</div>}
                    </div>
                </div>
                {!pendente && (
                    <div className="vr-readonly-banner">
                        {STATUS_LABEL[c.status]}{c.decided_by_email ? ` por ${c.decided_by_email} em ${brDateTime(c.decided_at)}` : ''}
                        {c.undone_by_email ? ` · desfeita por ${c.undone_by_email} em ${brDateTime(c.undone_at)}` : ''}
                        {c.decision_note ? ` — “${c.decision_note}”` : ''} — só leitura.
                    </div>
                )}
                {pendente && decisao && !decisao.pode && (
                    <div className="vr-readonly-banner wl-banner-warn">
                        Você pode revisar, mas não decidir: {decisao.motivo}
                        {decisao.relogin && <button className="vr-btn vr-btn-sm" style={{ marginLeft: 10 }} onClick={reentrar}>Entrar de novo</button>}
                    </div>
                )}
                {pendente && conflitoPrevisto && (
                    <div className="vr-readonly-banner wl-banner-warn">Algum config foi alterado aqui depois da proposta: a aprovação será recusada. Peça uma proposta nova.</div>
                )}
                <div className="vr-steps">
                    {STEPS.map((s) => (
                        <button key={s.key} className={`vr-step ${step === s.key ? 'active' : ''}`} onClick={() => goStep(s.key)}>{s.label}</button>
                    ))}
                </div>
            </div>

            <div className="vr-body">
                {sidePanel}
                <main className="vr-main">{content}</main>
            </div>
            <div className="vr-footer">{footer}</div>

            {modal?.kind === 'aprovar' && (
                <Modal wide title="Aplicar neste ambiente?" onClose={() => setModal(null)}
                    footer={<><button className="vr-btn" onClick={() => setModal(null)}>Cancelar</button>
                        <button className="vr-btn vr-btn-success" disabled={temAoVivo && !conferi}
                            onClick={() => { setModal(null); decidir('approve'); }}>Aprovar e aplicar</button></>}>
                    <p>Vão ser gravados {aplicaveis.length} config{aplicaveis.length > 1 ? 's' : ''} em <strong>{c.subdomain}</strong>, de uma vez. Se algum tiver sido alterado aqui depois da proposta, nada é aplicado.</p>
                    {temAoVivo && (
                        <div className="vr-mbox warn">
                            <h4>Vale na hora</h4>
                            <ul>{aplicaveis.filter(valeNaHora).map((it) => <li key={it.id}>{nomeItem(it)}{it.alcance ? ` — ${it.alcance.vendedores} vendedores` : ''}</li>)}</ul>
                            <label className="wl-check">
                                <input id="wl-conferi" type="checkbox" checked={conferi} onChange={(e) => setConferi(e.target.checked)} />
                                Conferi as diferenças e sei que estes configs mudam a tela dos vendedores agora.
                            </label>
                        </div>
                    )}
                    {aplicaveis.some(entraGuardado) && (
                        <div className="vr-mbox info">Os demais entram guardados: só valem depois de ativados no Whitelabel.</div>
                    )}
                    <p className="vr-muted vr-small">Dá para desfazer depois, enquanto ninguém editar esses configs.</p>
                </Modal>
            )}
            {modal?.kind === 'recusar' && (
                <Modal title="Recusar a proposta?" onClose={() => setModal(null)}
                    footer={<><button className="vr-btn" onClick={() => setModal(null)}>Cancelar</button>
                        <button className="vr-btn vr-btn-danger-outline" onClick={() => { setModal(null); decidir('reject'); }}>Recusar</button></>}>
                    Nada muda neste ambiente. A proposta fica registrada como recusada{nota ? ', com a sua observação' : ''}.
                </Modal>
            )}
            {modal?.kind === 'desfazer' && (
                <Modal title="Desfazer a proposta?" onClose={() => setModal(null)}
                    footer={<><button className="vr-btn" onClick={() => setModal(null)}>Cancelar</button>
                        <button className="vr-btn vr-btn-danger-outline" onClick={() => { setModal(null); decidir('undo'); }}>Desfazer</button></>}>
                    Os configs editados voltam ao conteúdo de antes e os criados são apagados. Se algum foi alterado depois da aprovação,
                    ou se um config criado já foi ativado, nada é desfeito.
                </Modal>
            )}
            {modal?.kind === 'erro' && (
                <Modal wide title={modal.conflito ? 'Não foi possível aplicar' : modal.relogin ? 'Entre de novo para decidir' : 'Erro'} onClose={() => setModal(null)}
                    footer={modal.relogin
                        ? <><button className="vr-btn" onClick={() => setModal(null)}>Fechar</button>
                            <button className="vr-btn vr-btn-primary" onClick={reentrar}>Entrar de novo</button></>
                        : <><button className="vr-btn" onClick={() => setModal(null)}>Fechar</button>
                            {modal.conflito && <button className="vr-btn vr-btn-primary" onClick={() => { setModal(null); setP(null); load(); }}>Recarregar a proposta</button>}</>}>
                    <p>{modal.text}</p>
                    {modal.conflitos.length > 0 && <ul>{modal.conflitos.map((x) => <li key={x}>{x}</li>)}</ul>}
                </Modal>
            )}
        </div>
    );
}

function PromocaoRevisaoRoute(props) {
    const { id } = useParams();
    return <PromocaoRevisao key={id} {...props} />;
}

export default PromocaoRevisaoRoute;
